import Link from 'next/link';
import camelCase from 'lodash/camelCase';

import { Button, Checkbox, Form, InputText, LabelProps, useForm } from '@dansk-metal/ui';

import { EventRegistrationInput, MemberDto, RegistrationFormDto, RegistrationInput, RegistrationSection } from '@web/services/crm-api/rest';

import styles from './event-registration-form.module.scss';

export type RegistrationFormData = {
	[x: string]: string;
};

export type RegistrationFormProps = {
	form: ReturnType<typeof useForm>;
	onSubmit: (data: RegistrationFormData) => void;
	formSections: RegistrationFormDto['sections'];
	member?: MemberDto | null;
	userContactInfoPageUrl: string;
	isSubmitting: boolean;
};

export function RegistrationForm({ onSubmit, form, formSections, member, userContactInfoPageUrl, isSubmitting }: RegistrationFormProps) {
	const sortedFormSections = formSections?.sort((a, b) => a.order - b.order);

	return (
		<form className={styles.container} onSubmit={form.handleSubmit(onSubmit)}>
			<div className={styles.head}>
				<h2>Tilmelding</h2>
			</div>

			<div className={styles.section}>
				<h3>Dine oplysninger</h3>
				<div className={styles.field}>
					{member && (
						<ul>
							<li>
								Navn:&nbsp;<strong>{member.firstName}</strong>
							</li>
							<li>
								Email:&nbsp;<strong>{member.email}</strong>
							</li>
							<li>
								Mobil:&nbsp;<strong>{member.phoneNumber}</strong>
							</li>
						</ul>
					)}
					<p>
						Har du ændringer til dine personlige oplysninger, kan du rette
						dem under <strong><Link className={styles.profile_link} href={userContactInfoPageUrl}>din data</Link></strong>.
					</p>
				</div>
			</div>

			{sortedFormSections && sortedFormSections.map((section) => {
				return (
					<div className={styles.section} key={section.label}>
						<h3>{section.label}</h3>
						{section.fields && section.fields.sort((a, b) => a.order - b.order).map((field) => (
							<div className={styles.field} key={field.key}>
								{getControlledInputField(field, section, form.control, isSubmitting)}
							</div>
						))}
					</div>
				);
			})}

			<Button icon={isSubmitting ? { icon: 'spinner', size: 24 } : { icon: 'arrow-right', size: 24 }} type='submit' className={styles.submit} disabled={isSubmitting}>
				Tilmeld
			</Button>
		</form>
	);
}

function getControlledInputField(
	input: RegistrationInput,
	section: RegistrationSection,
	controller: Form.Control<Form.FieldValues, unknown>,
	isSubmitting: boolean,
) {
	if (!input || !input.key) return null;
	const rules = { required: input.required ? `${input.key} er påkrævet` : false };

	const defaultLabelProps: Pick<LabelProps, 'required' | 'label' | 'noOptionalLabel'> = {
		required: input.required,
		label: input.key,
		noOptionalLabel: input.required,
	};

	// Key is a freetext field in crm and might contain invalid chars for react-hook-form
	const inputName = camelCase(section.label + input.key);

	switch (input.type) {
		case 'TextField':
			return (
				<Form.Controller
					name={inputName}
					control={controller}
					rules={rules}
					render={({ field, fieldState }) => (
						<InputText
							fieldState={fieldState}
							{...defaultLabelProps}
							{...field}
							disabled={isSubmitting}
						/>
					)}
				/>
			);
		case 'CheckBox':
			return (
				<Form.Controller
					name={inputName}
					control={controller}
					rules={rules}
					render={({ field, fieldState }) => (
						<Checkbox
							fieldState={fieldState}
							{...defaultLabelProps}
							{...field}
							disabled={isSubmitting}
						/>
					)}
				/>
			);
		default:
			return null;
	}
}

/**
 * Helper function to convert the form data to a format that can be sent to the CRM.
 */
export function buildRegistrationInputs(sections: RegistrationSection[], data: RegistrationFormData): EventRegistrationInput[] {
	const registrationInputs: EventRegistrationInput[] = sections.reduce((acc, section) => {
		return [
			...acc,
			...(section.fields || []).map((input) => ({
				sectionName: section.label as string,
				label: input.key as string,
				value: data[camelCase((section.label as string) + input.key as string)] as string,
			})),
		];
	}, []);

	return registrationInputs as EventRegistrationInput[];
}
